import { Disclosure } from "@headlessui/react";
import { ChevronRight } from "@material-ui/icons";
import { mainSelectedTab } from "modules/CarrierAgreementMgmt/atoms";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { useHistory } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { EmployeeTabs } from "modules/EmployeeTab/EmployeeTab";

const HierarchyLeadershipView = ({ data }) => {
  const history = useHistory();
  const setSelectedIndex = useSetRecoilState(mainSelectedTab);
  const userPermissions = useRecoilValue(permissionsSelector);

  const onClickEmployee = (employeeId, data) => {
    const url = "/employeetab/employee";
    history.push({
      pathname: url,
      state: {
        hierarchyId: data.hierarchyId,
        employeeId: employeeId,
        actionToManager: "LeadershipRedirection",
      },
    });

    let userTabs = [
      {
        to: "",
      },
    ];
    [...EmployeeTabs].forEach((tabs) => {
      let temp = [...userPermissions].filter(
        (user) => user === tabs.permission
      );
      if (temp.length > 0) userTabs.push(tabs);
    });
    userTabs.shift();
    const index = userTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };

  type RoleRowProps = {
    label: string;
    name: string;
    onClick: () => void;
  };

  type DisplayRowProps = {
    label: string;
    value?: React.ReactNode;
  };

  const RoleRow: React.FC<RoleRowProps> = ({ label, name, onClick }) => (
    <div className="grid grid-cols-3 p-2">
      <label className="fontExpand expandedLabel mx-4">{label}:</label>
      <label className="text-sm text-gray-700 text-left">
        <span className="cursor-pointer" onClick={onClick}>
          <div className="blueAndBold underline text-xs">{name}</div>
        </span>
      </label>
    </div>
  );

  const DisplayRow: React.FC<DisplayRowProps> = ({ label, value }) => (
    <div className="grid grid-cols-3 p-2">
      <label className="fontExpand expandedLabel mx-4">{label}</label>
      <div className="col-span-2 expandedData fontExpand">
        <label>{value}</label>
      </div>
    </div>
  );

  const displayFields = [
    { label: "Phone Number:", value: data?.phone },
    { label: "Email:", value: data?.email },
    {
      label: "Effective Date:",
      value: data?.managerEffectiveDate
        ? new Date(data.managerEffectiveDate).toLocaleDateString()
        : "",
    },
    {
      label: (() => {
        const managerLabelMap: Record<string, string> = {
          Team: "Previous Team Manager:",
          District: "Previous District Manager:",
          State: "Previous Director:",
          Market: "Previous Vice President:",
          "Business Unit": "Previous Senior Vice President:",
        };
        return (
          managerLabelMap[data?.hierarchyTypeName ?? "Business Unit"] ||
          "Previous Manager:"
        );
      })(),
      value: data?.previousManagerName,
    },
  ];

  const managerFields = [
    {
      label: "Director",
      name: data?.directorEmployeeName,
      id: data?.directorEmployeeId,
    },
    {
      label: "Vice President",
      name: data?.vicePresidentEmployeeName,
      id: data?.vicePresidentEmployeeId,
    },
    {
      label: "Senior Vice President",
      name: data?.seniorVicePresidentEmployeeName,
      id: data?.seniorVicePresidentEmployeeId,
    },
  ];
  return (
    <>
      <Disclosure defaultOpen>
        {({ open }) => (
          <>
            <div className="m-2 bg-white rounded-lg box-shadow">
              <Disclosure.Button className="disclosureBtn ml-2">
                <ChevronRight
                  className={`${
                    open ? "transform rotate-90" : " text-gray-500"
                  } text-gray-500 w-5 h-5 `}
                />
                <span className="disclosureLabel ml-2">Leadership</span>
              </Disclosure.Button>
              <Disclosure.Panel className="">
                <div className="grid grid-cols-1 md:grid-cols-2 text-xs py-2">
                  <div className="p-2 divide-y">
                    <div className="grid grid-cols-3 p-2">
                      <label className="fontExpand expandedLabel mx-4">
                        {(() => {
                          const managerLabelMap: Record<string, string> = {
                            Team: "Team Manager:",
                            District: "District Manager:",
                            State: "Director:",
                            Market: "Vice President:",
                            "Business Unit": "Senior Vice President:",
                          };

                          return managerLabelMap[
                            data?.hierarchyTypeName ?? "Business Unit"
                          ];
                        })()}
                      </label>
                      <label className="text-xs text-gray-700 text-left">
                        <span
                          className="cursor-pointer"
                          onClick={() => {
                            const employeeIdMap: Record<
                              string,
                              string | undefined
                            > = {
                              Frontline: data?.teamManagerEmployeeId,
                              Team: data?.teamManagerEmployeeId,
                              District: data?.districtManagerEmployeeId,
                              State: data?.directorEmployeeId,
                              Market: data?.vicePresidentEmployeeId,
                              "Business Unit":
                                data?.seniorVicePresidentEmployeeId,
                            };

                            const selectedEmployeeId =
                              employeeIdMap[data?.hierarchyTypeName ?? ""];

                            if (selectedEmployeeId) {
                              onClickEmployee(selectedEmployeeId, data);
                            }
                          }}
                        >
                          <div className="blueAndBold underline text-xs">
                            {(() => {
                              const employeeNames: Record<string, string> = {
                                Frontline: data?.teamManagerEmployeeName,
                                Team: data?.teamManagerEmployeeName,
                                District: data?.districtManagerEmployeeName,
                                State: data?.directorEmployeeName,
                                Market: data?.vicePresidentEmployeeName,
                                "Business Unit":
                                  data?.seniorVicePresidentEmployeeName,
                              };

                              return employeeNames[
                                data?.hierarchyTypeName ?? ""
                              ];
                            })()}
                          </div>
                        </span>
                      </label>
                    </div>
                    {displayFields.map((field, index) => (
                      <DisplayRow
                        key={index}
                        label={field.label}
                        value={field.value}
                      />
                    ))}
                  </div>
                  {(data?.hierarchyTypeName === "Team" ||
                    data?.hierarchyTypeName === "District") && (
                    <div className="p-2 divide-y">
                      {data?.hierarchyTypeName !== "District" && (
                        <RoleRow
                          label="District Manager"
                          name={data?.districtManagerEmployeeName}
                          onClick={() =>
                            onClickEmployee(
                              data?.districtManagerEmployeeId,
                              data
                            )
                          }
                        />
                      )}

                      {managerFields.map(({ label, name, id }) => (
                        <RoleRow
                          key={label}
                          label={label}
                          name={name}
                          onClick={() => onClickEmployee(id, data)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </Disclosure.Panel>
            </div>
          </>
        )}
      </Disclosure>
    </>
  );
};

export default HierarchyLeadershipView;
